import { YearMonthDay } from '@shared/types';
import { isValid } from 'date-fns';

const ymdRegex = /^ymd-(\d{4})-(\d{2})-(\d{2})$/;

export const ymdFromParts = (props: {
  year: number;
  month: number;
  day: number;
}): YearMonthDay => {
  return {
    year: props.year,
    month: props.month,
    day: props.day,
  };
};

/**
 * Parses a string representation of a date into a YearMonthDay object.
 *
 * @param str - The string representation of the date.
 * @param format - The format of the string representation.
 * @returns The parsed YearMonthDay object if the string is valid, otherwise undefined.
 */
export const ymdFromString = (props: {
  str: string;
  format: YmdStringFormat;
}): YearMonthDay | undefined => {
  const { str, format } = props;
  // Sanity check before parsing
  if (!isValid(new Date(str))) {
    return undefined;
  }

  let [year, month, day] = [-1, -1, -1];
  switch (format) {
    case 'yyyy-MM-dd': {
      [year, month, day] = str.split('-').map(Number);
      break;
    }
    case 'MM/dd/yyyy': {
      [month, day, year] = str.split('/').map(Number);
      break;
    }
    case 'M/d/yyyy': {
      [month, day, year] = str.split('/').map(Number);
      break;
    }
  }

  // Sanity check after parsing
  const partsValid =
    year >= 0 && month >= 1 && month <= 12 && day >= 1 && day <= 31;
  if (!partsValid) {
    return undefined;
  }
  return ymdFromParts({ year, month, day });
};

/**
 * Returns a string in the specified format.
 */
export const ymdToString = (props: {
  ymd: YearMonthDay;
  format: YmdStringFormat;
}) => {
  const { ymd, format } = props;
  // Note - Padding the year to 4 digits because we've seen dates with years
  // in the hundreds (e.g. 0202-01-01).
  switch (format) {
    case 'yyyy-MM-dd':
      return `${padNumber(ymd.year, 4)}-${padNumber(ymd.month, 2)}-${padNumber(
        ymd.day,
        2,
      )}`;
    case 'yyyyMMdd':
      return `${padNumber(ymd.year, 4)}${padNumber(ymd.month, 2)}${padNumber(
        ymd.day,
        2,
      )}`;
    case 'MM/dd/yyyy':
      return `${padNumber(ymd.month, 2)}/${padNumber(ymd.day, 2)}/${padNumber(
        ymd.year,
        4,
      )}`;
    case 'M/d/yyyy':
      return `${ymd.month}/${ymd.day}/${padNumber(ymd.year, 4)}`;
  }
};

export const ymdFromFirestoreString = (
  str: string,
): YearMonthDay | undefined => {
  const match = str.match(ymdRegex);
  if (!match) {
    return undefined;
  }
  const [, year, month, day] = match;
  return ymdFromParts({
    year: Number(year),
    month: Number(month),
    day: Number(day),
  });
};

export const ymdToFirestoreString = (ymd: YearMonthDay) => {
  return `ymd-${ymdToString({ ymd, format: 'yyyy-MM-dd' })}`;
};

export const ymdFromUTCDate = (date: Date): YearMonthDay => {
  return ymdFromParts({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
};

export const ymdFromLocalDate = (date: Date): YearMonthDay => {
  return ymdFromParts({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
};

export const ymdNow = (): YearMonthDay => {
  const date = new Date();
  return ymdFromParts({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
};

/**
 * Today's date in UTC.
 *
 * Use this — not `ymdNow()` — when comparing against transaction or invoice
 * dates that are derived from `Timestamp.toDate()` (which the
 * `MatterFinancialInfo.transactions()` filter does via `getUTCFullYear()`
 * etc.). Mixing local and UTC days causes a transaction created late in the
 * local evening (already past midnight UTC) to be excluded from "current"
 * balance computations.
 *
 * Use `ymdNow()` (local) for human-facing "today" — date pickers, validation
 * for "earned today", filenames, etc.
 */
export const ymdNowUtc = (): YearMonthDay => {
  return ymdFromUTCDate(new Date());
};

export const ymdToDate = (ymd: YearMonthDay) => {
  return new Date(ymd.year, ymd.month - 1, ymd.day);
};

/**
 * Returns `ymd`'s milliseconds since midnight, January 1, 1970 UTC.
 */
export const ymdToTime = (ymd: YearMonthDay) => {
  return ymdToDate(ymd).getTime();
};

export const ymdIsEqualTo = (props: {
  ymd: YearMonthDay;
  other: YearMonthDay;
}) => {
  const { ymd, other } = props;
  return ymdToTime(ymd) === ymdToTime(other);
};

/**
 * Checks if the current YearMonthDay is on or after the specified YearMonthDay.
 * @param other - The YearMonthDay to compare against.
 * @returns `true` if `this` YearMonthDay is on or after the `other` YearMonthDay, `false` otherwise.
 */
export const ymdIsOnOrAfter = (props: {
  ymd: YearMonthDay;
  other: YearMonthDay;
}) => {
  const { ymd, other } = props;
  return ymdToTime(ymd) >= ymdToTime(other);
};

/**
 * Checks if `ymd` is before `other`.
 * @returns `true` if `ymd` YearMonthDay is before `other`, `false` otherwise.
 */
export const ymdIsBefore = (props: {
  ymd: YearMonthDay;
  other: YearMonthDay;
}) => {
  const { ymd, other } = props;
  return ymdToTime(ymd) < ymdToTime(other);
};

export const ymdIsOnOrBefore = (props: {
  ymd: YearMonthDay;
  other: YearMonthDay;
}) => {
  const { ymd, other } = props;
  return ymdToTime(ymd) <= ymdToTime(other);
};

export const ymdIsAfter = (props: {
  ymd: YearMonthDay;
  other: YearMonthDay;
}) => {
  const { ymd, other } = props;
  return ymdToTime(ymd) > ymdToTime(other);
};

/**
 * Examples:
 *
 * `yyyy-MM-dd`:
 *   - `2022-01-01`
 *   - `2022-12-31`
 *
 * `MM/dd/yyyy`:
 *   - `01/01/2022`
 *   - `12/31/2022`
 *
 * `M/d/yyyy`:
 *   - `1/1/2022`
 *   - `12/31/2022`
 *
 * Format details: http://unicode.org/reports/tr35/tr35-6.html#Date_Format_Patterns
 */
export type YmdStringFormat =
  | 'yyyy-MM-dd'
  | 'yyyyMMdd'
  | 'MM/dd/yyyy'
  | 'M/d/yyyy';

/**
 * Sorts two YearMonthDay objects based on their values.
 *
 * @param ymd1 - The first YearMonthDay object to compare.
 * @param ymd2 - The second YearMonthDay object to compare.
 * @param descending - Optional. Specifies whether the sorting order should be descending. Default is false.
 * @returns A number indicating the sorting order:
 *          - 1 if ymd1 is greater than ymd2
 *          - -1 if ymd1 is less than ymd2
 *          - 0 if ymd1 is equal to ymd2
 *
 * @note Undefined values are always sorted to the end.
 */
export const ymdSortOrder = (
  ymd1: YearMonthDay | undefined,
  ymd2: YearMonthDay | undefined,
  descending: boolean = false,
) => {
  if (!ymd1 && !ymd2) {
    return 0;
  }
  if (!ymd1) {
    // Always sort undefined values to the end
    return 1;
  }
  if (!ymd2) {
    // Always sort undefined values to the end
    return -1;
  }

  const order = (ymdToTime(ymd1) ?? 0) - (ymdToTime(ymd2) ?? 0);
  return (order > 0 ? 1 : order < 0 ? -1 : 0) * (descending ? -1 : 1);
};

export const isYearMonthDay = (v: unknown): v is YearMonthDay => {
  if (!v || typeof v !== 'object') return false;
  const o = v as Partial<Record<'year' | 'month' | 'day', unknown>>;
  return (
    Number.isInteger(o.year) &&
    Number.isInteger(o.month) &&
    Number.isInteger(o.day) &&
    (o.month as number) >= 1 &&
    (o.month as number) <= 12 &&
    (o.day as number) >= 1 &&
    (o.day as number) <= 31
  );
};

// -----------------------------------------------------------------------------
// Helper functions
// -----------------------------------------------------------------------------

const padNumber = (num: number, length: number) => {
  return num.toString().padStart(length, '0');
};
